import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, ReferenceLine, ReferenceArea, ComposedChart } from 'recharts';
import { MealImpact } from '@/hooks/useExperimentResults';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ChevronDown, Filter, Sparkles, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import * as Sentry from '@sentry/react';
import { useExperimentParticipants } from '@/hooks/useExperimentParticipants';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Participant } from '@/hooks/useParticipants';
import { validateExperiment } from '@/lib/experimentValidation';

interface AggregateExperimentResultsProps {
  results: MealImpact[];
  experimentId?: string;
}

interface ProductInfo {
  name: string;
  image?: string;
}

interface AggregateDataPoint {
  time: number; // minutes
  average: number;
  min: number;
  max: number;
  percentile90: number;
  count: number;
}

const AggregateExperimentResults: React.FC<AggregateExperimentResultsProps> = ({ results, experimentId }) => {
  // Participants and user-based filters
  const { data: participants = [] } = useExperimentParticipants(experimentId || "");

  const availableGlycemicTypes = useMemo(() => {
    return ['Non-diabetic', 'Type 1 Diabetic', 'Type 2 Diabetic', 'Pre-diabetic'];
  }, []);

  const availableAgeGroups = useMemo(() => {
    const set = new Set<string>();
    participants.forEach((p: Participant) => p.ageGroup && set.add(p.ageGroup));
    return Array.from(set).sort();
  }, [participants]);

  const availableGenders = useMemo(() => {
    const set = new Set<string>();
    participants.forEach((p: Participant) => p.gender && set.add(p.gender));
    return Array.from(set).sort();
  }, [participants]);

  // Selected filters (default to all)
  const [selectedGlycemicTypes, setSelectedGlycemicTypes] = useState<Set<string>>(() => new Set());
  const [selectedAgeGroups, setSelectedAgeGroups] = useState<Set<string>>(() => new Set());
  const [selectedGenders, setSelectedGenders] = useState<Set<string>>(() => new Set());
  const [includeInvalid, setIncludeInvalid] = useState<boolean>(() => {
    if (!experimentId) return false;
    const saved = localStorage.getItem(`experiment-${experimentId}-include-invalid`);
    return saved ? JSON.parse(saved) : false;
  });

  // Temp states for dropdowns
  const [glycemicTypesMenuOpen, setGlycemicTypesMenuOpen] = useState(false);
  const [ageMenuOpen, setAgeMenuOpen] = useState(false);
  const [genderMenuOpen, setGenderMenuOpen] = useState(false);
  const [tempGlycemicTypes, setTempGlycemicTypes] = useState<Set<string>>(new Set());
  const [tempAges, setTempAges] = useState<Set<string>>(new Set());
  const [tempGenders, setTempGenders] = useState<Set<string>>(new Set());

  // Insights state
  const [insights, setInsights] = useState<string>('');
  const [loadingInsights, setLoadingInsights] = useState(false);

  // Initialize selections to all when options load
  useEffect(() => {
    if (availableGlycemicTypes.length && selectedGlycemicTypes.size === 0) setSelectedGlycemicTypes(new Set(availableGlycemicTypes));
    if (availableAgeGroups.length && selectedAgeGroups.size === 0) setSelectedAgeGroups(new Set(availableAgeGroups));
    if (availableGenders.length && selectedGenders.size === 0) setSelectedGenders(new Set(availableGenders));
  }, [availableGlycemicTypes, availableAgeGroups, availableGenders]);

  // Sync temp when menus open
  useEffect(() => { if (glycemicTypesMenuOpen) setTempGlycemicTypes(new Set(selectedGlycemicTypes)); }, [glycemicTypesMenuOpen, selectedGlycemicTypes]);
  useEffect(() => { if (ageMenuOpen) setTempAges(new Set(selectedAgeGroups)); }, [ageMenuOpen, selectedAgeGroups]);
  useEffect(() => { if (genderMenuOpen) setTempGenders(new Set(selectedGenders)); }, [genderMenuOpen, selectedGenders]);

  // Persist includeInvalid
  useEffect(() => {
    if (experimentId) localStorage.setItem(`experiment-${experimentId}-include-invalid`, JSON.stringify(includeInvalid));
  }, [includeInvalid, experimentId]);

  const applyGlycemicTypes = () => { setSelectedGlycemicTypes(new Set(tempGlycemicTypes)); setGlycemicTypesMenuOpen(false); };
  const applyAges = () => { setSelectedAgeGroups(new Set(tempAges)); setAgeMenuOpen(false); };
  const applyGenders = () => { setSelectedGenders(new Set(tempGenders)); setGenderMenuOpen(false); };

  // Helpers for temporary selection management
  const toggleValue = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    value: string
  ) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const toggleTempGlycemicType = (glycemicType: string) => toggleValue(setTempGlycemicTypes, glycemicType);
  const toggleTempAge = (age: string) => toggleValue(setTempAges, age);
  const toggleTempGender = (gender: string) => toggleValue(setTempGenders, gender);

  const selectAllGlycemicTypes = () => setTempGlycemicTypes(new Set(availableGlycemicTypes));
  const selectNoneGlycemicTypes = () => setTempGlycemicTypes(new Set());
  const cancelGlycemicTypes = () => { setTempGlycemicTypes(new Set(selectedGlycemicTypes)); setGlycemicTypesMenuOpen(false); };

  const selectAllAges = () => setTempAges(new Set(availableAgeGroups));
  const selectNoneAges = () => setTempAges(new Set());
  const cancelAges = () => { setTempAges(new Set(selectedAgeGroups)); setAgeMenuOpen(false); };

  const selectAllGenders = () => setTempGenders(new Set(availableGenders));
  const selectNoneGenders = () => setTempGenders(new Set());
  const cancelGenders = () => { setTempGenders(new Set(selectedGenders)); setGenderMenuOpen(false); };

  const glycemicTypesChanged = useMemo(() => {
    if (tempGlycemicTypes.size !== selectedGlycemicTypes.size) return true;
    for (const g of tempGlycemicTypes) if (!selectedGlycemicTypes.has(g)) return true;
    return false;
  }, [tempGlycemicTypes, selectedGlycemicTypes]);

  const agesChanged = useMemo(() => {
    if (tempAges.size !== selectedAgeGroups.size) return true;
    for (const a of tempAges) if (!selectedAgeGroups.has(a)) return true;
    return false;
  }, [tempAges, selectedAgeGroups]);

  const gendersChanged = useMemo(() => {
    if (tempGenders.size !== selectedGenders.size) return true;
    for (const g of tempGenders) if (!selectedGenders.has(g)) return true;
    return false;
  }, [tempGenders, selectedGenders]);
  // Map participants by userId for fast lookups
  const participantsByUserId = useMemo(() => {
    const m = new Map<string, Participant>();
    participants.forEach((p: Participant) => m.set(p.userId, p));
    return m;
  }, [participants]);

  // Filter results by user filters and validity
  const filteredResults = useMemo(() => {
    const allGlycemicTypes = selectedGlycemicTypes.size === availableGlycemicTypes.length;
    const allAges = selectedAgeGroups.size === availableAgeGroups.length;
    const allGenders = selectedGenders.size === availableGenders.length;

    return results.filter((r) => {
      if (!includeInvalid) {
        const p = participantsByUserId.get(r.user_id);
        const isDiabetic = p?.tags?.includes('Diabetic') || false;
        const validation = validateExperiment(r, isDiabetic);
        if (!validation.isValid) return false;
      }
      const p = participantsByUserId.get(r.user_id);
      if (!p) return true; // no participant metadata, keep
      if (!allGlycemicTypes && !selectedGlycemicTypes.has(p.glycemicType)) return false;
      if (!allAges && !selectedAgeGroups.has(p.ageGroup)) return false;
      if (!allGenders && !selectedGenders.has(p.gender)) return false;
      return true;
    });
  }, [results, includeInvalid, participantsByUserId, selectedGlycemicTypes, selectedAgeGroups, selectedGenders, availableGlycemicTypes.length, availableAgeGroups.length, availableGenders.length]);

  const generateInsights = async () => {
    if (chartData.length === 0 || filteredResults.length === 0) {
      toast.error("No data available to generate insights");
      return;
    }

    setLoadingInsights(true);
    try {
      // Prepare data for AI analysis (based on current user filters)
      const filtersSummary = `glycemic types: ${selectedGlycemicTypes.size === availableGlycemicTypes.length ? 'All' : Array.from(selectedGlycemicTypes).join(', ')}, ages: ${selectedAgeGroups.size === availableAgeGroups.length ? 'All' : Array.from(selectedAgeGroups).join(', ')}, genders: ${selectedGenders.size === availableGenders.length ? 'All' : Array.from(selectedGenders).join(', ')}, includeInvalid: ${includeInvalid ? 'yes' : 'no'}`;

      const glucoseAnalysisData = filteredResults
        .filter(result => result.satiety_score != null)
        .map(result => ({
          products: result.food_log_names,
          peak_glucose: result.peak_glucose_level - result.start_glucose_level,
          time_to_peak: result.time_to_peak,
          iauc: result.iauc,
          glycemic_range: result.glycemic_range,
          indicative_glycemic: result.indicative_glycemic,
          satiety_score: result.satiety_score
        }));

      const aiPrompt = `Given the following glucose response data and satiety scores from a nutrition experiment, please generate some single sentence conclusions about the statistics, referencing the aggregate results. For example 'X% of participants experienced a peak glucose response within 30 minutes' or 'Product Y showed 30% lower peak glucose levels compared to Product X' or 'Z% of participants reported feeling satisfied 2 hours after the meal'.

Data Summary:
- Filters: ${filtersSummary}
- Number of participants: ${filteredResults.length}
- Chart data points (time in minutes vs normalized glucose mg/dL): ${JSON.stringify(chartData.slice(0, 12))}
- Glucose and satiety analysis data: ${JSON.stringify(glucoseAnalysisData.slice(0, 8))}
- Average peak glucose: ${glucoseAnalysisData.length > 0 ? (glucoseAnalysisData.reduce((sum, d) => sum + d.peak_glucose, 0) / glucoseAnalysisData.length).toFixed(1) : 'N/A'} mg/dL
- Average time to peak: ${glucoseAnalysisData.length > 0 ? (glucoseAnalysisData.reduce((sum, d) => sum + d.time_to_peak, 0) / glucoseAnalysisData.length).toFixed(1) : 'N/A'} minutes
- Average satiety score: ${glucoseAnalysisData.length > 0 ? (glucoseAnalysisData.reduce((sum, d) => sum + d.satiety_score, 0) / glucoseAnalysisData.length).toFixed(1) : 'N/A'} (1=very hungry, 5=very full)

Please provide 3-5 key insights as bullet points, focusing on glucose response patterns, peak times, glycemic impact, and satiety levels across the selected cohort.`;

      const { data, error } = await supabase.functions.invoke('openai-chat', {
        body: {
          messages: [
            { role: 'user', content: aiPrompt }
          ],
          model: 'gpt-4o-mini',
          temperature: 0.3
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      setInsights(data.content);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      Sentry.captureException(error);
      toast.error('Failed to generate insights: ' + errorMessage);
    } finally {
      setLoadingInsights(false);
    }
  };

  // Process and normalize all glucose samples into 15-minute buckets
  const processAggregateData = (): AggregateDataPoint[] => {
    // Create 15-minute buckets from -30min to +180min
    const buckets = new Map<number, number[]>();
    for (let time = -30; time <= 180; time += 15) {
      buckets.set(time, []);
    }

    // Normalize all samples and group into buckets
    filteredResults.forEach((result) => {
      if (!result.glucose_samples || result.glucose_samples.length === 0) return;

      // Calculate baseline from pre-meal samples (-30 to 0 minutes)
      const baselineSamples = result.glucose_samples.filter(sample => {
        const timeInMinutes = sample[0] / 60;
        return timeInMinutes >= -30 && timeInMinutes <= 0;
      });

      let calculatedBaseline = result.start_glucose_level; // fallback
      if (baselineSamples.length > 0) {
        const baselineSum = baselineSamples.reduce((sum, sample) => sum + sample[1], 0);
        calculatedBaseline = baselineSum / baselineSamples.length;
      }

      result.glucose_samples.forEach((sample) => {
        const timeInMinutes = sample[0] / 60; // Convert seconds to minutes
        const normalizedGlucose = sample[1] - calculatedBaseline; // Normalize to calculated baseline

        // Find the appropriate bucket (round to nearest 15-minute interval)
        const bucketTime = Math.round(timeInMinutes / 15) * 15;

        // Only include if within our range
        if (bucketTime >= -30 && bucketTime <= 180 && buckets.has(bucketTime)) {
          buckets.get(bucketTime)!.push(normalizedGlucose);
        }
      });
    });

    // Calculate statistics for each bucket
    const aggregateData: AggregateDataPoint[] = [];

    buckets.forEach((values, timeInMinutes) => {
      if (values.length === 0) {
        // No data for this bucket, skip or use null values
        return;
      }

      const sortedValues = values.sort((a, b) => a - b);
      const average = values.reduce((sum, val) => sum + val, 0) / values.length;
      const min = Math.min(...values);
      const max = Math.max(...values);

      // Calculate 90th percentile
      const percentile90Index = Math.floor(0.9 * (sortedValues.length - 1));
      const percentile90 = sortedValues[percentile90Index];

      aggregateData.push({
        time: timeInMinutes,
        average,
        min,
        max,
        percentile90,
        count: values.length
      });
    });

    // Sort by time
    return aggregateData.sort((a, b) => a.time - b.time);
  };

  const chartData = processAggregateData();
  const hasData = chartData.length > 0;
  const yAxisMin = -30;
  const yAxisMax = 180;
  // Visible domain for clamping the target range
  const xMin = hasData ? chartData[0].time : -30;
  const xMax = hasData ? chartData[chartData.length - 1].time : 180;
  const y1Target = Math.max(yAxisMin, -15);
  const y2Target = Math.min(yAxisMax, 45);

  // Identify the individual traces with highest and lowest peak (relative to baseline)
  const peakInfo = useMemo(() => {
    const arr: { idx: number; peak: number }[] = [];
    filteredResults.forEach((res, idx) => {
      const samples = res.glucose_samples || [];
      if (!samples.length) return;
      const start = res.start_glucose_level;
      let localMax = -Infinity;
      for (const s of samples) {
        const delta = s[1] - start;
        if (delta > localMax) localMax = delta;
      }
      if (localMax !== -Infinity) arr.push({ idx, peak: localMax });
    });
    if (arr.length === 0) return { max: null as null | { idx: number; peak: number }, min: null as null | { idx: number; peak: number } };
    let max = arr[0];
    let min = arr[0];
    for (const a of arr) {
      if (a.peak > max.peak) max = a;
      if (a.peak < min.peak) min = a;
    }
    return { max, min };
  }, [filteredResults]);

  if (!results || results.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Aggregate Experiment Results</CardTitle>
          <CardDescription>
            Average glucose response across all participants
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            No experiment results available for aggregation.
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartConfig = {
    percentile90: {
      label: "90th Percentile",
      color: "hsl(var(--primary))",
    },
    individual: {
      label: "Individual experiment results",
      color: "hsl(var(--muted-foreground))",
    },
    targetRange: {
      label: "Target range",
      color: "hsl(var(--chart-green))",
    },
    maxLine: {
      label: "Max peak line",
      color: "hsl(var(--chart-1))",
    },
    minLine: {
      label: "Min peak line",
      color: "hsl(var(--chart-2))",
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Aggregate Experiment Results</CardTitle>
        <CardDescription>
          Averaged glucose response in 15-minute intervals with min-max range and 90th percentile ({filteredResults.length} of {results.length} data points, normalized to baseline)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filters:</span>

          {/* Glycemic Types filter */}
          <DropdownMenu open={glycemicTypesMenuOpen} onOpenChange={setGlycemicTypesMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Glycemic Types: {selectedGlycemicTypes.size === availableGlycemicTypes.length ? 'All' : `${selectedGlycemicTypes.size}`} <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="z-50 w-80 bg-popover" onCloseAutoFocus={(e) => e.preventDefault()}>
              <div className="flex gap-2 p-2 border-b">
                <Button variant="ghost" size="sm" onClick={selectAllGlycemicTypes} className="flex-1">Select All</Button>
                <Button variant="ghost" size="sm" onClick={selectNoneGlycemicTypes} className="flex-1">Select None</Button>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {availableGlycemicTypes.map(glycemicType => (
                  <DropdownMenuCheckboxItem
                    key={glycemicType}
                    checked={tempGlycemicTypes.has(glycemicType)}
                    onCheckedChange={() => toggleTempGlycemicType(glycemicType)}
                    className="flex items-center gap-3"
                    onSelect={(e) => e.preventDefault()}
                  >
                    <span className="flex-1">{glycemicType}</span>
                  </DropdownMenuCheckboxItem>
                ))}
              </div>
              {glycemicTypesChanged && (
                <>
                  <DropdownMenuSeparator />
                  <div className="flex gap-2 p-2">
                    <Button size="sm" onClick={applyGlycemicTypes} className="flex-1">Apply ({tempGlycemicTypes.size})</Button>
                    <Button variant="outline" size="sm" onClick={cancelGlycemicTypes} className="flex-1">Cancel</Button>
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Age group filter */}
          <DropdownMenu open={ageMenuOpen} onOpenChange={setAgeMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Age: {selectedAgeGroups.size === availableAgeGroups.length ? 'All' : `${selectedAgeGroups.size}`} <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="z-50 w-80 bg-popover" onCloseAutoFocus={(e) => e.preventDefault()}>
              <div className="flex gap-2 p-2 border-b">
                <Button variant="ghost" size="sm" onClick={selectAllAges} className="flex-1">Select All</Button>
                <Button variant="ghost" size="sm" onClick={selectNoneAges} className="flex-1">Select None</Button>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {availableAgeGroups.map(age => (
                  <DropdownMenuCheckboxItem
                    key={age}
                    checked={tempAges.has(age)}
                    onCheckedChange={() => toggleTempAge(age)}
                    className="flex items-center gap-3"
                    onSelect={(e) => e.preventDefault()}
                  >
                    <span className="flex-1">{age}</span>
                  </DropdownMenuCheckboxItem>
                ))}
              </div>
              {agesChanged && (
                <>
                  <DropdownMenuSeparator />
                  <div className="flex gap-2 p-2">
                    <Button size="sm" onClick={applyAges} className="flex-1">Apply ({tempAges.size})</Button>
                    <Button variant="outline" size="sm" onClick={cancelAges} className="flex-1">Cancel</Button>
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Gender filter */}
          <DropdownMenu open={genderMenuOpen} onOpenChange={setGenderMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Gender: {selectedGenders.size === availableGenders.length ? 'All' : `${selectedGenders.size}`} <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="z-50 w-80 bg-popover" onCloseAutoFocus={(e) => e.preventDefault()}>
              <div className="flex gap-2 p-2 border-b">
                <Button variant="ghost" size="sm" onClick={selectAllGenders} className="flex-1">Select All</Button>
                <Button variant="ghost" size="sm" onClick={selectNoneGenders} className="flex-1">Select None</Button>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {availableGenders.map(g => (
                  <DropdownMenuCheckboxItem
                    key={g}
                    checked={tempGenders.has(g)}
                    onCheckedChange={() => toggleTempGender(g)}
                    className="flex items-center gap-3"
                    onSelect={(e) => e.preventDefault()}
                  >
                    <span className="flex-1">{g}</span>
                  </DropdownMenuCheckboxItem>
                ))}
              </div>
              {gendersChanged && (
                <>
                  <DropdownMenuSeparator />
                  <div className="flex gap-2 p-2">
                    <Button size="sm" onClick={applyGenders} className="flex-1">Apply ({tempGenders.size})</Button>
                    <Button variant="outline" size="sm" onClick={cancelGenders} className="flex-1">Cancel</Button>
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center gap-2 ml-auto">
            <Label htmlFor="include-invalid" className="text-sm">Include invalid</Label>
            <Switch id="include-invalid" checked={includeInvalid} onCheckedChange={setIncludeInvalid} />
          </div>
        </div>
        <div className="w-full">
          <ChartContainer config={chartConfig} className="w-full h-[400px]">
            <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <XAxis
                dataKey="time"
                type="number"
                scale="linear"
                domain={['dataMin', 'dataMax']}
                ticks={[-30, 0, 30, 60, 90, 120, 150, 180]}
                tickFormatter={(value) => {
                  if (value === 0) return "Meal";
                  if (value < 0) return `${Math.abs(value)} min before`;
                  return `${value} min after`;
                }}
              />
              <YAxis
                domain={[yAxisMin, yAxisMax]}
                tickFormatter={(value) => `${value > 0 ? '+' : ''}${value} mg/dL`}
              />

              {/* Target range: -15 to +45 mg/dL (green horizontal band, clamped to visible range) */}
              <ReferenceArea
                x1={xMin}
                x2={xMax}
                y1={y1Target}
                y2={y2Target}
                fill="hsl(var(--chart-green))"
                fillOpacity={0.08}
                stroke="hsl(var(--chart-green))"
                strokeOpacity={0.2}
                strokeDasharray="2 2"
              />

              {/* Meal reference line */}
              <ReferenceLine
                x={0}
                stroke="hsl(var(--primary))"
                strokeDasharray="3 3"
                label={{ value: "Meal", position: "insideTopLeft" }}
              />

              {/* Baseline reference line */}
              <ReferenceLine
                y={0}
                stroke="hsl(var(--muted-foreground))"
                strokeDasharray="3 3"
                label={{ value: "Baseline", position: "insideTopRight" }}
              />

              {/* Individual experiment traces */}
              {filteredResults.map((res, idx) => {
                const data = (res.glucose_samples || []).map((s) => ({
                  time: s[0] / 60,
                  value: s[1] - res.start_glucose_level,
                }));
                const isMax = peakInfo.max?.idx === idx;
                const isMin = peakInfo.min?.idx === idx;
                return (
                  <Line
                    key={`trace-${idx}`}
                    type="monotone"
                    data={data}
                    dataKey="value"
                    stroke={isMax ? 'var(--color-maxLine)' : isMin ? 'var(--color-minLine)' : 'hsl(var(--muted-foreground))'}
                    strokeOpacity={isMax || isMin ? 0.9 : 0.15}
                    strokeWidth={isMax || isMin ? 2 : 1}
                    dot={false}
                    isAnimationActive={false}
                  />
                );
              })}

              {/* 90th percentile line (thick purple) */}
              <Line
                type="monotone"
                dataKey="percentile90"
                stroke="var(--color-percentile90)"
                strokeWidth={4}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />

              {/* Legend */}
              <ChartLegend
                payload={[
                  { id: 'percentile90', type: 'line', value: '90th Percentile', color: 'var(--color-percentile90)' },
                  { id: 'maxLine', type: 'line', value: 'Max peak line', color: 'var(--color-maxLine)' },
                  { id: 'minLine', type: 'line', value: 'Min peak line', color: 'var(--color-minLine)' },
                  { id: 'individual', type: 'circle', value: 'Individual experiment results', color: 'var(--color-individual)' },
                  { id: 'targetRange', type: 'rect', value: 'Target range', color: 'var(--color-targetRange)' },
                ]}
                content={<ChartLegendContent nameKey="id" />}
              />

              <ChartTooltip
                content={({ active, payload, label }) => {
                  if (!active) return null;

                  const p90Entry = Array.isArray(payload)
                    ? (payload as Array<{ dataKey?: string; payload?: AggregateDataPoint }>).find((p) => p?.dataKey === 'percentile90')
                    : undefined;
                  const data = p90Entry?.payload as AggregateDataPoint | undefined;

                  const buildTimeLabel = (lbl: unknown) => {
                    if (typeof lbl !== 'number') return '';
                    if (lbl === 0) return 'Meal';
                    if (lbl < 0) return `${Math.abs(lbl)} min before`;
                    return `${lbl} min after`;
                  };

                  // Only show tooltip when aggregate data (percentile) is present; ignore individual traces
                  if (!data) {
                    return null;
                  }

                  const timeLabel = buildTimeLabel(label);

                  const avg = typeof data.average === 'number' ? data.average : undefined;
                  const p90 = typeof data.percentile90 === 'number' ? data.percentile90 : undefined;
                  const min = typeof data.min === 'number' ? data.min : undefined;
                  const max = typeof data.max === 'number' ? data.max : undefined;

                  return (
                    <div className="bg-background border border-border/50 rounded-lg p-2 shadow-xl">
                      <span className="font-mono text-sm">{p90 != null ? `${p90 > 0 ? '+' : ''}${p90.toFixed(1)} mg/dL` : 'N/A'}</span>
                    </div>
                  );
                }}
              />
             </ComposedChart>
          </ChartContainer>
        </div>

        <div className="mt-4 text-sm text-muted-foreground">
          <p>• All glucose values are normalized to each participant's baseline glucose level</p>
          <p>• Thick purple line shows the 90th percentile response</p>
          <p>• Faint lines show individual experiment traces</p>
          <p>• Green band marks the target range (-15 to +45 mg/dL)</p>
        </div>

        {/* Get Insights Section */}
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">AI Insights</h3>
            <Button
              onClick={generateInsights}
              disabled={loadingInsights || filteredResults.length === 0}
              variant="outline"
              size="sm"
            >
              {loadingInsights ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Get Insights
                </>
              )}
            </Button>
          </div>

          {insights && (
            <Card className="bg-muted/30">
              <CardContent className="p-4">
                <div className="prose prose-sm max-w-none text-foreground [&>*]:text-foreground">
                  <ReactMarkdown>{insights}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AggregateExperimentResults;